/**
 * Vendor slug + Notion-photo folder helpers — MS26.
 *
 * Vendors don't have a `slug` column in vendor_partners, so we
 * derive one deterministically from `vendor_name`. The slug is
 * stable as long as the name is stable; if a vendor renames,
 * we'd need a redirect (not a problem today — none have).
 *
 * Lucky's Notion-curated photos live under
 * /public/vendor-photos/<folder>/. The folder names came from
 * MS25's download script and don't always 1:1 match the slug
 * we derive here (e.g. "Pinot & Picasso Melbourne" → slug
 * "pinot-picasso-melbourne", but Lucky's folder is
 * "pinot-picasso"). PHOTO_FOLDER_OVERRIDES maps slugs to
 * folder names where they diverge.
 */

/**
 * Convert a vendor display name into a stable URL slug.
 * Strips diacritics, drops punctuation, kebab-cases.
 */
export function vendorSlug(name: string): string {
  return name
    // Strip accents (Chéri → Cheri)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    // Treat & as "and" for readability
    .replace(/&/g, " and ")
    // Replace any non-alphanumeric run with a single hyphen
    .replace(/[^a-zA-Z0-9]+/g, "-")
    // Collapse leading/trailing hyphens + lowercase
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

/**
 * Map slug → /public/vendor-photos/<folder>/ name.
 * Defaults to the slug itself; only overrides when Lucky's
 * Notion folder name differs.
 */
const PHOTO_FOLDER_OVERRIDES: Record<string, string> = {
  "pinot-and-picasso-melbourne": "pinot-picasso",
  "peninsula-hot-springs": "peninsula-hot-springs",
  "st-ali": "stali",
  // MS46b: Aunty Peg's → Proud Mary's. Aunty Peg's is one of Proud
  // Mary's cafes; the parent roastery name is the canonical brand.
  "proud-mary-s": "proud-marys",
  "maker-and-monger": "maker-and-monger",
  // MS295: Larkspur Flowers' DB vendor_name → slug "larkspur-flowers"
  // but the Notion-curated photos landed in /public/vendor-photos/
  // larkspur/ (folder predates the MS281 vendor insert which used
  // the full "Larkspur Flowers" brand name). Map the slug back to
  // the existing folder so the per-vendor page renders photos.
  "larkspur-flowers": "larkspur",
};

export function vendorPhotoFolder(slug: string): string {
  return PHOTO_FOLDER_OVERRIDES[slug] ?? slug;
}

/**
 * Hardcoded photo manifest — which slug has photos in
 * /public/vendor-photos/, and what filenames. Used by both the
 * runtime VendorPage AND the prerender script. This is generated
 * at MS25 download time; if more vendors get photos via Notion,
 * re-run scripts/sync-vendor-photos.mjs (TODO future sprint) and
 * update this manifest.
 */
export const VENDOR_PHOTO_MANIFEST: Record<string, string[]> = {
  // MS46b: was "aunty-pegs". Same photos, renamed folder.
  "proud-marys": ["coffee3.jpg", "coffee4.jpg", "coffee5.jpg"],
  "botanique-workshop": [
    "flowers12.jpg",
    "flowers11.jpg",
    "flowers10.jpg",
    "flowers9.jpg",
    "flowers8.webp",
  ],
  "cecilia-fox": ["flowers_5.jpg", "flowers6.jpg", "flowers7.jpg"],
  "inner-studio": [
    "innerstudio1.jpg",
    "innerstudio2.jpg",
    "innerstudio3.jpg",
    "innerstudio5.jpg",
  ],
  "larkspur": [
    "flowers1.jpg",
    "flowers2.jpg",
    "flowers3.jpg",
    "flowera4.jpg",
  ],
  "maker-and-monger": [
    "hampers1.webp",
    "hampers2.webp",
    "hampers3.webp",
    "hampers4.jpg",
  ],
  "market-lane": ["coffee1.webp", "coffee2.jpg"],
  // MS46: swapped to Lucky's stronger picnic-trio shot. Patricia.avif
  // is no longer the primary asset; picnic-trio.jpg + picnic-3.jpg
  // (alternate angle) are the live ones.
  "patricia-coffee": ["picnic-trio.jpg", "picnic-3.jpg"],
  "peninsula-hot-springs": [
    "spa1.jpg",
    "spa2.jpg",
    "spa3.jpg",
    "spa4.jpg",
  ],
  "pinot-picasso": ["sip_paint.jpg", "sip_paint2.webp", "sip_paint3.jpg"],
  // 2026-05-16: Lucky uploaded 3 bathhouse interior hero shots to
  // his Sense of Self Notion sub-page specifically to fix the
  // original "you don't even show the actual spa" complaint. The
  // voucher-card.jpg in the same folder is for SKU rows (already
  // wired); these three are vendor-page hero + gallery candidates.
  "sense-of-self": ["hero-1.jpg", "hero-2.jpg", "hero-3.jpg"],
  // MS493: Lucky's Comma bathhouse photos (tub, sauna, amenities).
  comma: ["bathhouse-tub.jpg", "sauna.jpg", "spa-amenities.jpg"],
  "stali": ["coffee6.webp", "coffee7.webp"],
};

/**
 * Returns absolute paths to a vendor's curated photos (or null).
 * Looks up by slug → folder via PHOTO_FOLDER_OVERRIDES, then
 * matches against the manifest.
 */
export function getVendorPhotos(slug: string): string[] | null {
  const folder = vendorPhotoFolder(slug);
  const files = VENDOR_PHOTO_MANIFEST[folder];
  if (!files || files.length === 0) return null;
  return files.map((f) => `/vendor-photos/${folder}/${f}`);
}
